/**
 * A small copy of a catalogue photo, asked for from the host that serves it.
 *
 * Catalogue photos are stored at around 1000px. The picklist shows them in
 * 40-48px tiles, and the printed picklist embedded every one at full size,
 * which made the PDF slow to open and scroll. Both hosts we store photos on
 * will resize from the same object:
 *
 * - Supabase Storage public objects move from `/storage/v1/object/public/` to
 *   `/storage/v1/render/image/public/` and gain `width`, `height` and
 *   `resize=contain`. Matched on the path, not the hostname, so a custom
 *   Supabase domain still works. Signed URLs are left alone: the signature
 *   covers the path.
 * - Shopify CDN URLs gain `width` and `height`.
 *
 * `resize=contain` fits the whole image inside the box instead of cropping, so
 * the full garment stays visible. Existing query params (`?v=`, `?t=`) are
 * kept. Anything else, or a bad `px`, returns the url unchanged.
 */
const SUPABASE_PUBLIC_OBJECT_PREFIX = '/storage/v1/object/public/';
const SUPABASE_PUBLIC_RENDER_PREFIX = '/storage/v1/render/image/public/';

export function thumbnailUrl(url: string | null | undefined, px: number): string | null {
  if (!url) return null;
  if (!Number.isFinite(px) || px <= 0) return url;
  const size = Math.round(px);
  if (size <= 0) return url;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  if (parsed.pathname.startsWith(SUPABASE_PUBLIC_OBJECT_PREFIX)) {
    parsed.pathname =
      SUPABASE_PUBLIC_RENDER_PREFIX + parsed.pathname.slice(SUPABASE_PUBLIC_OBJECT_PREFIX.length);
    parsed.searchParams.set('width', String(size));
    parsed.searchParams.set('height', String(size));
    parsed.searchParams.set('resize', 'contain');
    return parsed.toString();
  }

  if (parsed.hostname === 'cdn.shopify.com') {
    parsed.searchParams.set('width', String(size));
    parsed.searchParams.set('height', String(size));
    return parsed.toString();
  }

  return url;
}

/**
 * Pulls `url` into the browser cache without painting it, so a later img tag
 * showing the same url is instant. Never set crossOrigin here: the request
 * must match the plain img tags that show the url, or it misses the cache and
 * downloads twice. `low` is for a background warm-up that must never compete
 * with what is already on screen.
 */
export function warmImage(url: string, { low = false }: { low?: boolean } = {}): void {
  const img = new Image();
  if (low) {
    img.fetchPriority = 'low';
    img.decoding = 'async';
  }
  img.src = url;
}
